import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { MaskPresetKey, resolveMaskWithOptions } from '@/shared/constants';
import { Field } from '@noorden-insurtech/alba-design-system';
import { ReactNode } from 'react';
import { Mask, withMask } from 'use-mask-input';
import type { TFieldContext } from '../types';

export type TextFieldProps = {
  label: string;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  disabled?: boolean;
  maxLength?: number;
  mask?: Mask | MaskPresetKey;
  onBlur?: () => void | Promise<void>;
  suffixIcon?: ReactNode;
  prefixIcon?: ReactNode;
  /** Converte o que é digitado/colado para minúsculas (ex.: e-mail). */
  lowercase?: boolean;
};

export function createTextField(useFieldContext: () => unknown) {
  return function TextField({
    label,
    placeholder,
    required,
    helperText,
    disabled,
    maxLength,
    mask,
    onBlur: customOnBlur,
    suffixIcon,
    prefixIcon,
    lowercase,
  }: TextFieldProps) {
    const field = useFieldContext() as TFieldContext<string>;

    const [resolvedMask, resolvedMaskOptions] = mask
      ? resolveMaskWithOptions(mask)
      : [undefined, undefined];

    const handleBlur = () => {
      field.handleBlur();
      customOnBlur?.();
    };

    return (
      <RequiredFieldTooltip active={required}>
        <Field.Root invalid={!!field.state.meta.errors.length}>
          <Field.Label>
            {label}
            {required && <span className='text-alba-error-red-500'>*</span>}
          </Field.Label>
        <div className='relative'>
          {prefixIcon && (
            <div className='absolute left-3 top-1/2 transform -translate-y-1/2'>
              {prefixIcon}
            </div>
          )}
          <Field.Input
            ref={resolvedMask ? withMask(resolvedMask, resolvedMaskOptions) : undefined}
            value={field.state.value || ''}
            onChange={e =>
              field.handleChange(
                lowercase ? e.target.value.toLowerCase() : e.target.value
              )
            }
            onBlur={handleBlur}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
          />
          {suffixIcon && (
            <div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
              {suffixIcon}
            </div>
          )}
        </div>
        {helperText && !field.state.meta.errors.length && (
          <Field.HelperText>{helperText}</Field.HelperText>
        )}
        {field.state.meta.errors.length > 0 && (
          <Field.ErrorText>{field.state.meta.errors[0]}</Field.ErrorText>
        )}
        </Field.Root>
      </RequiredFieldTooltip>
    );
  };
}
